import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import useModalEffect from '../../../hooks/useModalEffect';

const ExpenseCategoryForm = ({ isOpen, onClose, onSubmit, initialData }) => {
  useModalEffect(isOpen);
  const [formData, setFormData] = useState({
    name: ''
  });
  const [formErrors, setFormErrors] = useState({});

  useEffect(() => {
    if (initialData) {
      setFormData({
        name: initialData.name || ''
      });
    } else {
      setFormData({ name: '' });
    }
  }, [initialData, isOpen]);

  // Modal animation effects
  ;

  // Cleanup effect when component unmounts
  useEffect(() => {
    return () => {
      // Clean up any remaining modal states when component unmounts
      const cleanupModal = () => {
        // Remove modal scroll lock
        document.body.classList.remove('modal-open');
        document.body.style.overflow = '';
        document.body.style.paddingRight = '';
        document.body.classList.remove('modal-backdrop');
      };
      
      cleanupModal();
    };
  }, []);


  // Cleanup effect: reset modal body state when component unmounts.
  useEffect(() => {
    return () => {
      // React owns the backdrop DOM (createPortal); only reset body state here.
      // Remove modal-open class from body
      document.body.classList.remove('modal-open');
    };
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.name.trim()) {
      errors.name = 'Name is required';
    }
    return errors;
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    const errors = validateForm();
    if (Object.keys(errors).length === 0) {
      onSubmit(formData);
      setFormData({ name: '' });
    } else {
      setFormErrors(errors);
    }
  };

  const handleModalClose = () => {
    setFormData({ name: '' });
    setFormErrors({});
    onClose();
  };

  return (
    <>
      <style>{`
        #modal-expense-category .inv-section-label {
          font-size: 0.7rem; font-weight: 700; letter-spacing: 0.08em;
          text-transform: uppercase; color: #6c7a7d; margin-bottom: 0.75rem;
          display: flex; align-items: center; gap: 0.4rem;
        }
        #modal-expense-category .inv-section-label::after {
          content: ''; flex: 1; height: 1px; background: #e9ecef;
        }
        #modal-expense-category .inv-footer {
          border-top: 1px solid #e9ecef !important;
          background: #fff;
          padding: 1rem 1.5rem;
          display: flex; align-items: center; justify-content: flex-end; gap: 0.6rem;
          flex-shrink: 0;
        }
        #modal-expense-category .btn-inv-save {
          display: inline-flex; align-items: center; gap: 0.4rem;
        }
        #modal-expense-category .btn-inv-save:disabled {
          opacity: 0.6; cursor: not-allowed;
        }
        #modal-expense-category .inv-required-star { color: #e53935; margin-left: 2px; }
      `}</style>
      {isOpen && ReactDOM.createPortal(<div className="modal-backdrop fade show" style={{ zIndex: 1040 }} />, document.body)}
      <div
        className={`modal modal-blur fade${isOpen ? ' show' : ''}`} style={{ display: isOpen ? 'block' : 'none' }}
        id="modal-expense-category"
        tabIndex="-1"
        role="dialog"
      >
        <div className="modal-dialog modal-md modal-dialog-centered" role="document">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">{initialData ? 'Edit Expense Category' : 'Create Expense Category'}</h5>
              <button
                type="button"
                className="btn-close"
                onClick={onClose}
                aria-label="Close"
              ></button>
            </div>
            <form onSubmit={handleFormSubmit}>
              <div className="modal-body">
                <div className="inv-section-label">
                  <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/></svg>
                  Details
                </div>
                <div className="mb-3">
                  <label className="form-label">Name <span className="text-danger">*</span></label>
                  <input
                    type="text"
                    className={`form-control ${formErrors.name ? 'is-invalid' : ''}`}
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="Enter expense category name"
                  />
                  {formErrors.name && (
                    <div className="invalid-feedback">{formErrors.name}</div>
                  )}
                </div>
              </div>
              <div className="inv-footer">
                <span className="text-muted me-auto" style={{ fontSize: '0.75rem' }}>
                  <span style={{ color: '#9aa5b1' }}>
                    <span className="inv-required-star">*</span> Required fields
                  </span>
                </span>
                <button type="button" className="btn btn-outline-secondary" onClick={onClose}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary btn-inv-save">
                  <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                  Save
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

ExpenseCategoryForm.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  initialData: PropTypes.object
};

export default ExpenseCategoryForm;

